import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import styles from './scrollable-pills.module.scss';

interface ScrollablePillsData {
  id: string;
  text: string;
  icon?: string;
  onClickItem?(): void;
}

interface ScrollablePillsProps {
  data: Array<ScrollablePillsData>;
  selectedItemId: string;
  onClick(id: string): void;
  className?: string;
  noBackground?: boolean;
  disabled?: boolean;
}

export function ScrollablePills({className = '', disabled = false, noBackground = false, ...props}: ScrollablePillsProps): ReactElement | null {
  const [listRef, setListRef] = useState<HTMLUListElement | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);

  useEffect((): void => {
    let selectedElement = null;
    if (listRef) {
      const collection = listRef.children;
      for (let i = 0; i < collection.length; i++) {
        if (collection[i]?.classList.contains('selected-pill-item')) {
          selectedElement = collection[i];
        }
      }
    }

    if (selectedElement) {
      // @ts-ignore
      setOffsetLeft(selectedElement?.offsetLeft + selectedElement.getBoundingClientRect().width / 2);
    }
  }, [props.selectedItemId, listRef]);

  const onClickHandler = (id: string): void => {
    props.onClick(id);
  };

  const getItems = (): Array<ReactElement> => {
    return props.data.map((item) => {
      return (
        <li
          onClick={(): void => {
            if (disabled) {
              return;
            }
            onClickHandler(item.id);
            if (item.onClickItem) {
              item.onClickItem();
            }
          }}
          key={item.id}
          className={`flex-center radius-round ${styles.item} ${props.selectedItemId === item.id ? styles.selected : ''} ${
            item.id === props.selectedItemId ? `color-primary-darker selected-pill-item` : ''
          } ${noBackground ? styles.noBackground : ''}`}
        >
          {item.icon ? (
            <Icon
              isDisabled={disabled}
              icon={item.icon}
              size={IconSize.SIZE_ICON_16}
              className={`${styles.icon} ${props.selectedItemId === item.id ? 'color-primary-darker' : ''}`}
            />
          ) : null}
          <Text disableSelection={disabled} val={item.text} bold size={TextSize.XSMALL} />
        </li>
      );
    });
  };

  return (
    <div className={`${styles.container} flex-center ${className}`}>
      <ul
        ref={setListRef}
        style={{
          left: `calc(50% - ${offsetLeft}px`,
          transition: 'left 0.2s ease-in-out',
        }}
        className={`${styles.list} spacing-m-0 flex-h-row`}
      >
        {getItems()}
      </ul>
    </div>
  );
}
